import {
  AfterViewInit,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  EventEmitter,
  inject,
  Injector,
  Input,
  NgZone,
  OnDestroy,
  Output,
  PLATFORM_ID,
  signal,
  Signal,
  untracked,
  ViewChild,
  WritableSignal
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { isPlatformBrowser, registerLocaleData } from '@angular/common';
import localeNlBE from '@angular/common/locales/nl-BE';
import {
  ActionSheetController,
  IonButton,
  IonIcon,
  IonModal,
  IonSegment,
  IonSegmentButton,
  IonSpinner,
  IonText,
  LoadingController,
} from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import {
  arrowUpRightBoxOutline,
  checkmarkOutline,
  chevronBack,
  chevronDown,
  chevronForward,
  chevronUp,
  close,
  documentOutline,
  imageOutline,
  logoLinkedin,
  logoWhatsapp,
  mailOutline,
} from 'ionicons/icons';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ChartConfiguration, ChartData, ChartDataset, TooltipItem } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import { DateTime, Settings as LuxonSettings } from 'luxon';
import { EnergyType, PriceItemDto, UnitTypeEnum, ViewType, VolumeEnum } from '../models';
import { DateFilterRange, DateNavigationService } from '../services/date-navigation.service';
import { ExportPdfCommand, ExportShareImageCommand, ShareService } from '../services/share.service';
import { CurrencyPipe } from '@angular/common';
import { COLORS } from '../colors';

// Register nl-BE locale data so Angular's CurrencyPipe works without requiring
// the consuming app to register it themselves.
registerLocaleData(localeNlBE, 'nl-BE');

// Register Ionicons at module load time (before any component constructor runs) so that
// Stencil's ion-icon web component can resolve icon data during SSR hydration.
addIcons({
  arrowUpRightBoxOutline,
  checkmarkOutline,
  chevronBack,
  chevronDown,
  chevronForward,
  chevronUp,
  close,
  documentOutline,
  imageOutline,
  logoLinkedin,
  logoWhatsapp,
  mailOutline,
});

const QUARTER_PAGE_SIZE = 16;

/** Maps the app language codes onto the Belgian locales Luxon should format dates with. */
const LUXON_LOCALES: Record<string, string> = {
  nl: 'nl-BE',
  fr: 'fr-BE',
  en: 'en-GB',
};
const DEFAULT_LUXON_LOCALE = LUXON_LOCALES['nl'];

@Component({
  selector: 'day-ahead-widget',
  templateUrl: 'day-ahead-widget.component.html',
  styleUrls: ['day-ahead-widget.component.scss'],
  standalone: true,
  imports: [
    IonIcon,
    IonSegment,
    IonSegmentButton,
    IonButton,
    IonText,
    IonModal,
    IonSpinner,
    BaseChartDirective,
    TranslatePipe,
    CurrencyPipe,
  ]
})
export class DayAheadWidgetComponent implements AfterViewInit, OnDestroy {
  private readonly dateNavigationService = inject(DateNavigationService);
  private readonly actionSheetCtrl = inject(ActionSheetController);
  private readonly loadingCtrl = inject(LoadingController);
  private readonly translateService = inject(TranslateService);
  private readonly shareService = inject(ShareService);
  private readonly hostEl = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly zone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);
  /** Public energy-prices page — what the web dialog's "copy URL" button yields. */
  private static readonly PRICES_PAGE_URL = 'https://enwyse.be/nl/energieprijzen/';

  readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  /** True only when running as a native Capacitor app (iOS/Android). False in all browser contexts. */
  readonly isNativeApp: boolean = typeof window !== 'undefined' && (window as any).Capacitor?.isNativePlatform?.() === true;
  private resizeObserver: ResizeObserver | undefined;

  /** Increments whenever translations load or the active language changes, so computed
   *  signals that call instant() re-run automatically. */
  private readonly _langVersion: WritableSignal<number> = signal(0);

  @ViewChild(BaseChartDirective) chart: BaseChartDirective<'bar'> | undefined;
  @ViewChild('rangePickerModal') rangePickerModal!: IonModal;
  @ViewChild('chartWrapper') chartWrapper!: ElementRef<HTMLDivElement>;
  @ViewChild('tableWrapper') tableWrapper!: ElementRef<HTMLDivElement>;
  @ViewChild('shareDialog') private shareDialog!: ElementRef<HTMLDialogElement>;

  /** Automatically set to true when the widget's own width exceeds 640 px. */
  desktopLayout: WritableSignal<boolean> = signal(false);

  @Input() set initialDate(value: DateTime | undefined) {
    if (value) {
      this.dateNavState.currentDate.set(value);
      this.isInitialDateSet.set(true);
      this.desktopCalendarMonth.set(value.startOf('month'));
      if (!this.latestAvailableDayDate()) {
        this.latestAvailableDayDate.set(value.startOf('day'));
      }
    }
  }

  @Input() set latestAvailableDate(value: DateTime | undefined) {
    if (value) {
      this.latestAvailableDayDate.set(value.startOf('day'));
    }
  }

  @Input() set rawData(value: PriceItemDto[]) {
    this._rawData.set(value ?? []);
    this.committedTimeRange.set(this.activeTimeRange());
    this.isLoadingChart.set(false);
  }

  @Input() set lastUpdated(value: DateTime | undefined) {
    this._lastUpdated.set(value);
  }

  @Output() dateChange: EventEmitter<WidgetApiPayload> = new EventEmitter<WidgetApiPayload>();

  isLoading: WritableSignal<boolean> = signal(true);
  isInitialDateSet: WritableSignal<boolean> = signal(false);

  activeMarketType: WritableSignal<MarketType> = signal('EL');
  activeVolume: WritableSignal<VolumeEnum> = signal(VolumeEnum.MWh);
  activeTimeRange: WritableSignal<TimeRange> = signal('DayHour');
  activeView: WritableSignal<WidgetView> = signal('chart');
  showAverage: WritableSignal<boolean> = signal(true);
  currentQuarterPage: WritableSignal<number> = signal(0);
  isTimeRangeDropdownOpen: WritableSignal<boolean> = signal(false);
  isShareDropdownOpen: WritableSignal<boolean> = signal(false);
  copyLinkSuccess: WritableSignal<boolean> = signal(false);
  committedTimeRange: WritableSignal<TimeRange> = signal('DayHour');
  isLoadingChart: WritableSignal<boolean> = signal(false);

  quarterPages: Signal<number[]> = computed(() =>
    Array.from({ length: this.totalQuarterPages() }, (_, i) => i)
  );

  private touchStartX = 0;
  private readonly SWIPE_THRESHOLD_PX = 50;
  private readonly earliestSelectableDate = DateTime.fromISO('2024-01-01').startOf('day');

  private readonly _rawData: WritableSignal<PriceItemDto[]> = signal([]);
  private readonly latestAvailableDayDate: WritableSignal<DateTime | undefined> = signal(undefined);

  hasNoData: Signal<boolean> = computed(() =>
    this.isInitialDateSet() && !this.isLoadingChart() && this._rawData().length === 0
  );
  private readonly _lastUpdated: WritableSignal<DateTime | undefined> = signal(undefined);

  lastUpdatedDisplay: Signal<DateTime | undefined> = computed(() => this._lastUpdated());

  totalQuarterPages: Signal<number> = computed(() =>
    Math.ceil(this._rawData().length / QUARTER_PAGE_SIZE)
  );

  displayedItems: Signal<PriceItemDto[]> = computed(() => {
    const items = this._rawData();
    if (this.committedTimeRange() === 'DayQuarter') {
      const page = this.currentQuarterPage();
      return items.slice(page * QUARTER_PAGE_SIZE, (page + 1) * QUARTER_PAGE_SIZE);
    }
    return items;
  });

  tableItems: Signal<PriceItemDto[]> = computed(() => this._rawData());

  averageValue: Signal<number> = computed(() => {
    const items = this.displayedItems();
    if (!items.length) return 0;
    return items.reduce((sum, i) => sum + (i.price ?? 0), 0) / items.length;
  });

  tableAverageValue: Signal<number> = computed(() => {
    const items = this.tableItems();
    if (!items.length) return 0;
    return items.reduce((sum, i) => sum + (i.price ?? 0), 0) / items.length;
  });

  minValue: Signal<number> = computed(() => {
    const items = this.displayedItems();
    if (!items.length) return 0;
    return Math.min(...items.map(i => i.price ?? 0));
  });

  maxValue: Signal<number> = computed(() => {
    const items = this.displayedItems();
    if (!items.length) return 0;
    return Math.max(...items.map(i => i.price ?? 0));
  });

  tableMinValue: Signal<number> = computed(() => {
    const items = this.tableItems();
    if (!items.length) return 0;
    return Math.min(...items.map(i => i.price ?? 0));
  });

  tableMaxValue: Signal<number> = computed(() => {
    const items = this.tableItems();
    if (!items.length) return 0;
    return Math.max(...items.map(i => i.price ?? 0));
  });

  legendItems: Signal<{ color: string; label: string }[]> = computed(() => {
    const data = this.chartData();
    return data.datasets.map(dataset => {
      const isLine = (dataset as any).type === 'line';
      let color: string;
      if (isLine) {
        color = (dataset as any).borderColor as string;
      } else {
        const legendColor = (dataset as any)._legendColor as string | undefined;
        if (legendColor) {
          color = legendColor;
        } else {
          const bgColors = dataset.backgroundColor as string[];
          color = Array.isArray(bgColors) ? bgColors[0] : (bgColors as unknown as string);
        }
      }
      return { color, label: dataset.label ?? '' };
    });
  });

  chartData: Signal<ChartData<'bar'>> = computed(() => {
    this._langVersion(); // re-run when translations load or language changes
    const items = this.displayedItems();
    const timeRange = this.committedTimeRange();
    const market = this.activeMarketType();
    const defaultColor = market === 'GAS' ? COLORS.PURPLE : COLORS.LIGHTBLUE;
    const min = this.tableMinValue();
    const max = this.tableMaxValue();

    const labels = items.map(item => {
      if (!item.date) return '';
      const dt = DateTime.fromJSDate(item.date).setLocale(this.luxonLocale);
      switch (timeRange) {
        case 'DayQuarter':
        case 'DayHour': return dt.toFormat('HH:mm');
        case 'Week':
        case 'Month': return dt.toFormat('dd/MM');
        case 'Year': return dt.toFormat('MMMM');
        default: return dt.toFormat('dd/MM/yyyy');
      }
    });

    const prices = items.map(i => i.price ?? 0);
    const backgroundColor = prices.map(p =>
      (p === min || p === max) && items.length > 0 ? this.highlightColor : defaultColor
    );

    const datasets: ChartDataset<'bar'>[] = [{
      label: this.translateService.instant(this.getLegendLabelKey(market, timeRange)),
      data: prices,
      backgroundColor,
      order: 2,
      _legendColor: defaultColor,
    } as ChartDataset<'bar'> & { _legendColor: string }];

    if (this.showAverage() && items.length > 0) {
      const avg = this.tableAverageValue();
      const avgFormatted = new Intl.NumberFormat('nl-BE', { style: 'currency', currency: 'EUR' }).format(avg);
      datasets.push({
        type: 'line',
        label: `${this.translateService.instant('COMMON.AVERAGE')} (${avgFormatted})`,
        data: labels.map(() => avg),
        borderColor: this.highlightColor,
        borderDash: [6, 4],
        borderWidth: 0,
        backgroundColor: this.highlightColor,
        pointRadius: 0,
        order: 1,
      } as unknown as ChartDataset<'bar'>);
    }

    return { labels, datasets };
  });

  dateNavState = this.dateNavigationService.createDateNavigation(DateTime.now().startOf('day'));

  desktopCalendarMonth: WritableSignal<DateTime> = signal(DateTime.now().startOf('month'));

  desktopCalendarDays: Signal<(DateTime | null)[]> = computed(() => {
    const month = this.desktopCalendarMonth();
    const startOfMonth = month.startOf('month');
    const dayOffset = startOfMonth.weekday - 1; // ISO: 1=Mon → offset 0, 7=Sun → offset 6
    const daysCount = month.daysInMonth ?? 30;
    const blanks: null[] = Array(dayOffset).fill(null);
    const days = Array.from({ length: daysCount }, (_, i) => startOfMonth.plus({ days: i }));
    return [...blanks, ...days];
  });

  desktopCalendarWeeks: Signal<DateTime[]> = computed(() => {
    const month = this.desktopCalendarMonth();
    const startOfMonth = month.startOf('month');
    const endOfMonth = month.endOf('month');
    const weeks: DateTime[] = [];
    let current = startOfMonth.startOf('week');
    while (current <= endOfMonth) {
      weeks.push(current);
      current = current.plus({ weeks: 1 });
    }
    return weeks;
  });

  desktopCalendarMonths: Signal<DateTime[]> = computed(() => {
    const year = this.desktopCalendarMonth().startOf('year');
    return Array.from({ length: 12 }, (_, i) => year.plus({ months: i }));
  });

  desktopCalendarYears: Signal<DateTime[]> = computed(() => {
    const year = this.desktopCalendarMonth().startOf('year');
    return Array.from({ length: 11 }, (_, i) => year.minus({ years: 5 - i }))
      .filter(candidateYear => candidateYear >= this.earliestSelectableDate.startOf('year'));
  });

  desktopCalendarHeader: Signal<string> = computed(() => {
    this._langVersion(); // re-run when language changes
    const month = this.desktopCalendarMonth();
    const range = this.activeFilterRange();
    if (range === 'Year') {
      const years = this.desktopCalendarYears();
      return `${years[0].toFormat('yyyy')} – ${years[years.length - 1].toFormat('yyyy')}`;
    }
    if (range === 'Month') return month.toFormat('yyyy');
    return month.setLocale(this.luxonLocale).toFormat('MMMM yyyy');
  });

  public barChartType = 'bar' as const;
  volumeEnum = VolumeEnum;

  private readonly highlightColor = '#D81B60';

  readonly chartPlugins = [{
    id: 'fullWidthAverageLine',
    afterDatasetsDraw: (chart: any): void => {
      const lineIdx = (chart.data.datasets as any[]).findIndex((d: any) => d.type === 'line');
      if (lineIdx === -1 || !chart.chartArea) return;
      const dataset = chart.data.datasets[lineIdx];
      if (!(dataset.data as number[]).length) return;

      const value = (dataset.data as number[])[0];
      const { ctx, chartArea: { left, right }, scales: { y } } = chart;
      const yPos = y.getPixelForValue(value);

      ctx.save();
      ctx.beginPath();
      ctx.setLineDash([6, 4]);
      ctx.strokeStyle = dataset.borderColor;
      ctx.lineWidth = 2;
      ctx.moveTo(left, yPos);
      ctx.lineTo(right, yPos);
      ctx.stroke();
      ctx.restore();
    }
  }];

  barChartOptions: ChartConfiguration<'bar'>['options'] = {
    maintainAspectRatio: false,
    layout: { padding: { bottom: 8 } },
    scales: {
      x: {
        grid: { display: false },
        ticks: { padding: 4 },
      },
      y: {
        grid: { display: false },
        ticks: {
          maxTicksLimit: 8,
          callback: (value) => `€ ${(+value).toFixed(2)}`,
        },
      },
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          title: (tooltipItems: TooltipItem<'bar'>[]) => {
            const firstTooltipItem = tooltipItems[0];
            const label = firstTooltipItem.label;
            let labelDateTime: DateTime = DateTime.fromFormat(label, 'dd/MM/yyyy');
            if (!labelDateTime.isValid) {
              const year = this.currentChartRange.start.year;
              const tryWithYear = DateTime.fromFormat(`${label}/${year}`, 'dd/MM/yyyy');
              if (tryWithYear.isValid) {
                labelDateTime = tryWithYear;
              } else {
                const tryMonth = DateTime.fromFormat(label, 'MMMM', { locale: this.luxonLocale });
                if (tryMonth.isValid) {
                  labelDateTime = tryMonth;
                } else {
                  const tryIso = DateTime.fromISO(label);
                  labelDateTime = tryIso.isValid ? tryIso : this.currentChartRange.start;
                }
              }
            }
            const viewType = this.committedTimeRange();
            switch (viewType) {
              case 'DayQuarter':
              case 'DayHour': {
                const currentDate = this.currentChartRange.start;
                const timeParts = label.split(':');
                const labelHour = Number(timeParts[0]);
                const labelMinute = Number(timeParts[1]);
                const modifiedTime = currentDate.set({ hour: labelHour, minute: labelMinute });
                const intervalMinutes = viewType === 'DayQuarter' ? 15 : 60;
                const addedTime = modifiedTime.plus({ minutes: intervalMinutes });
                return `${modifiedTime.toFormat('HH:mm')} - ${addedTime.toFormat('HH:mm')}`;
              }
              case 'Week':
              case 'Month': return labelDateTime.toFormat('dd/MM');
              case 'Year': return labelDateTime.setLocale(this.luxonLocale).toFormat('MMMM');
            }
            return firstTooltipItem.label;
          },
          label: (tooltipItem: TooltipItem<'bar'>) => {
            let label = tooltipItem.dataset.label || '';
            if (label) label += ': ';
            if (tooltipItem.parsed.y !== null) {
              label += new Intl.NumberFormat('nl-BE', { style: 'currency', currency: 'EUR' }).format(tooltipItem.parsed.y!);
            }
            return label;
          }
        }
      }
    }
  };

  /** Desktop variant: fills the flex panel instead of maintaining aspect ratio. */
  barChartOptionsDesktop: ChartConfiguration<'bar'>['options'] = {
    ...this.barChartOptions,
    maintainAspectRatio: false,
  };

  constructor() {
    // Luxon otherwise falls back to the browser/system locale, which renders month
    // names in whatever language the visitor's device is set to (English on most
    // non-Belgian setups) instead of the widget's own language.
    this.applyLuxonLocale();

    this.translateService.onTranslationChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this._langVersion.update(v => v + 1));

    this.translateService.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.applyLuxonLocale();
        this._langVersion.update(v => v + 1);
      });


    const payload$ = toObservable(
      computed<WidgetApiPayload | null>(() => {
        if (!this.isInitialDateSet()) return null;
        const timeRange = this.activeTimeRange();
        return {
          startDate: this.currentChartRange.start.toJSDate(),
          endDate: this.currentChartRange.end.toJSDate(),
          energyType: this.toEnergyType(this.activeMarketType()),
          viewType: this.toViewType(timeRange),
          filterRange: this.toDateFilterRange(timeRange),
          volume: this.activeVolume(),
          dayResolution: timeRange === 'DayQuarter' ? 'quarter' : timeRange === 'DayHour' ? 'hour' : undefined,
        };
      }),
      { injector: this.injector }
    );

    payload$
      .pipe(
        debounceTime(150),
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(payload => {
        if (!payload) return;
        this.isLoadingChart.set(true);
        this.dateChange.emit(payload);
      });

    effect(() => {
      this._rawData();
      this.currentQuarterPage.set(0);
    }, { injector: this.injector });

    // When the active filter range changes, snap the desktop calendar to the current date's period.
    effect(() => {
      this.activeFilterRange(); // track range changes
      this.desktopCalendarMonth.set(untracked(() => this.currentDate()).startOf('month'));
    }, { injector: this.injector });

  }

  ngAfterViewInit(): void {
    if (!this.isBrowser) return;
    this.resizeObserver = new ResizeObserver(entries => {
      const width = entries[0]?.contentRect.width ?? 0;
      const next = width >= 640;
      if (next === this.desktopLayout()) return;
      this.zone.run(() => this.desktopLayout.set(next));
    });
    // Run outside zone so the ResizeObserver itself doesn't trigger CD on every resize tick
    this.zone.runOutsideAngular(() => {
      this.resizeObserver!.observe(this.hostEl.nativeElement);
    });
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  /** Locale Luxon must format with, derived from the active widget language. */
  private get luxonLocale(): string {
    const lang = this.translateService.currentLang ?? 'nl';
    return LUXON_LOCALES[lang.split('-')[0]] ?? DEFAULT_LUXON_LOCALE;
  }

  /** Language code the API must render its own date labels with, so a server-generated
   *  PDF/PNG matches the language the widget's own labels in the same request use. */
  private get exportLang(): string {
    return this.translateService.currentLang ?? 'nl';
  }

  /** Makes every `toFormat()` call in the widget (and its services) use the widget language. */
  private applyLuxonLocale(): void {
    LuxonSettings.defaultLocale = this.luxonLocale;
  }

  get currentChartRange() { return this.dateNavState.currentChartRange(); }
  get currentDate() { return this.dateNavState.currentDate; }
  get activeFilterRange() { return this.dateNavState.activeFilterRange; }
  get rangePickerDates() { return this.dateNavState.rangePickerDates(); }

  get currentTimeDisplay(): string {
    const range = this.activeFilterRange();
    const date = this.currentDate();

    let prefix: string;
    let dateStr: string;

    switch (range) {
      case 'Day': {
        prefix = this.translateService.instant('COMMON.DAY');
        dateStr = date.setLocale(this.luxonLocale).toFormat('d MMMM yyyy');
        break;
      }
      case 'Week': {
        prefix = this.translateService.instant('COMMON.WEEK');
        const start = this.dateNavigationService.beginOfWeek(date);
        const end = this.dateNavigationService.endOfWeek(date);
        dateStr = `${start.toFormat('dd/MM')} - ${end.toFormat('dd/MM')}`;
        break;
      }
      case 'Month': {
        prefix = this.translateService.instant('COMMON.MONTH');
        dateStr = date.setLocale(this.luxonLocale).toFormat('MMMM yyyy');
        break;
      }
      case 'Year': {
        prefix = this.translateService.instant('COMMON.YEAR');
        dateStr = date.toFormat('yyyy');
        break;
      }
      default:
        return this.dateNavigationService.formatDate(date, range);
    }

    return `${prefix}: ${dateStr}`;
  }

  goBackward() { this.adjustCurrentDate(-1); }
  goForward() { this.adjustCurrentDate(1); }

  async updateCurrentDate(date: DateTime) {
    if (this.tryUpdateCurrentDate(date)) {
      await this.rangePickerModal.dismiss();
    }
  }

  isDateDisabled(date: DateTime): boolean {
    const range = this.activeFilterRange();
    const now = DateTime.now();

    switch (range) {
      case 'Day': {
        const day = date.startOf('day');
        const latestAvailableDay = this.latestAvailableDayDate();
        return day < this.earliestSelectableDate
          || (!!latestAvailableDay && day > latestAvailableDay);
      }
      case 'Week':
        return date.startOf('week') < this.earliestSelectableDate.startOf('week')
          || date.startOf('week') > now.startOf('week');
      case 'Month':
        return date.startOf('month') < this.earliestSelectableDate.startOf('month')
          || date.startOf('month') > now.startOf('month');
      case 'Year':
        return date.startOf('year') < this.earliestSelectableDate.startOf('year')
          || date.startOf('year') > now.startOf('year');
      default:
        return this.dateNavigationService.isDateDisabled(date);
    }
  }
  formatPickerDate(date: DateTime) { return this.dateNavigationService.formatPickerDate(date, this.activeFilterRange()); }
  beginOfWeek(date: DateTime) { return date.startOf('week'); }
  endOfWeek(date: DateTime) { return date.endOf('week'); }

  prevCalendarMonth(): void {
    if (this.isPrevCalendarMonthDisabled()) return;
    const range = this.activeFilterRange();
    if (range === 'Year') {
      this.desktopCalendarMonth.update(m => m.minus({ years: 10 }));
    } else if (range === 'Month') {
      this.desktopCalendarMonth.update(m => m.minus({ years: 1 }));
    } else {
      this.desktopCalendarMonth.update(m => m.minus({ months: 1 }));
    }
  }

  nextCalendarMonth(): void {
    if (this.isNextCalendarMonthDisabled()) return;
    const range = this.activeFilterRange();
    if (range === 'Year') {
      this.desktopCalendarMonth.update(m => m.plus({ years: 10 }));
    } else if (range === 'Month') {
      this.desktopCalendarMonth.update(m => m.plus({ years: 1 }));
    } else {
      this.desktopCalendarMonth.update(m => m.plus({ months: 1 }));
    }
  }

  isPrevCalendarMonthDisabled(): boolean {
    const range = this.activeFilterRange();
    if (range === 'Year') {
      return this.desktopCalendarMonth().minus({ years: 5 }).startOf('year') < this.earliestSelectableDate.startOf('year');
    }
    if (range === 'Month') {
      return this.desktopCalendarMonth().startOf('year') <= this.earliestSelectableDate.startOf('year');
    }
    return this.desktopCalendarMonth().startOf('month') <= this.earliestSelectableDate.startOf('month');
  }

  isNextCalendarMonthDisabled(): boolean {
    const range = this.activeFilterRange();
    const now = DateTime.now();
    if (range === 'Year') {
      return this.desktopCalendarMonth().plus({ years: 5 }).startOf('year') >= now.startOf('year');
    }
    if (range === 'Month') {
      return this.desktopCalendarMonth().startOf('year') >= now.startOf('year');
    }
    return this.desktopCalendarMonth().plus({ months: 1 }).startOf('month') > now.startOf('month');
  }

  isDesktopDaySelected(date: DateTime): boolean {
    return date.hasSame(this.currentDate(), 'day');
  }

  isDesktopPickerItemSelected(date: DateTime): boolean {
    const range = this.activeFilterRange();
    const current = this.currentDate();
    if (range === 'Week') return date.hasSame(current, 'week');
    if (range === 'Month') return date.hasSame(current, 'month');
    if (range === 'Year') return date.hasSame(current, 'year');
    return this.isDesktopDaySelected(date);
  }

  isDesktopPickerItemDisabled(date: DateTime): boolean {
    return this.isDateDisabled(date);
  }

  selectDesktopPickerItem(date: DateTime): void {
    if (!this.isDesktopPickerItemDisabled(date)) {
      this.tryUpdateCurrentDate(date);
    }
  }

  selectDesktopDay(date: DateTime): void {
    this.selectDesktopPickerItem(date);
  }

  getWeekLabel(date: DateTime): string {
    return `Week ${date.weekNumber}`;
  }

  getMonthLabel(date: DateTime): string {
    return date.setLocale(this.luxonLocale).toFormat('LLL');
  }

  getYearLabel(date: DateTime): string {
    return date.toFormat('yyyy');
  }

  canGoToPrevQuarterPage(): boolean { return this.currentQuarterPage() > 0; }
  canGoToNextQuarterPage(): boolean { return this.currentQuarterPage() < this.totalQuarterPages() - 1; }
  prevQuarterPage() { if (this.canGoToPrevQuarterPage()) this.currentQuarterPage.update(p => p - 1); }
  nextQuarterPage() { if (this.canGoToNextQuarterPage()) this.currentQuarterPage.update(p => p + 1); }

  goToPage(page: number): void {
    if (page >= 0 && page < this.totalQuarterPages()) {
      this.currentQuarterPage.set(page);
    }
  }

  onChartTouchStart(event: TouchEvent): void {
    this.touchStartX = event.touches[0].clientX;
  }

  onChartTouchEnd(event: TouchEvent): void {
    if (this.activeTimeRange() !== 'DayQuarter') return;
    const deltaX = event.changedTouches[0].clientX - this.touchStartX;
    if (Math.abs(deltaX) < this.SWIPE_THRESHOLD_PX) return;
    deltaX < 0 ? this.nextQuarterPage() : this.prevQuarterPage();
  }

  marketChange(changeEvent: CustomEvent): void {
    const marketType = changeEvent.detail.value as MarketType;
    if (marketType === 'GAS') {
      const current = this.activeTimeRange();
      if (current === 'DayQuarter' || current === 'DayHour') {
        this.activeTimeRange.set('Week');
        this.activeFilterRange.set('Week');
      }
    }
    this.activeMarketType.set(marketType);
  }

  rangeChange(changeEvent: CustomEvent): void {
    const timeRange = changeEvent.detail.value as TimeRange;
    this.activeTimeRange.set(timeRange);
    this.activeFilterRange.set(this.toDateFilterRange(timeRange));
  }

  toggleTimeRangeDropdown(): void {
    this.isTimeRangeDropdownOpen.update(open => !open);
  }

  selectTimeRange(range: TimeRange): void {
    this.activeTimeRange.set(range);
    this.activeFilterRange.set(this.toDateFilterRange(range));
    this.isTimeRangeDropdownOpen.set(false);
  }

  viewChange(changeEvent: CustomEvent): void {
    this.activeVolume.set(changeEvent.detail.value);
  }

  async openShareSheet(): Promise<void> {
    if (!this.isNativeApp) {
      if (this.isShareDropdownOpen()) {
        this.closeShareModal();
      } else {
        this.isShareDropdownOpen.set(true);
        this.shareDialog.nativeElement.showModal();
      }
      return;
    }

    const actionSheet = await this.actionSheetCtrl.create({
      header: this.translateService.instant('COMMON.SHARE'),
      cssClass: 'role-change-action-sheet',
      buttons: [
        { text: this.translateService.instant('COMMON.IMAGE'), icon: 'image-outline',    handler: () => { void this.handleShareImage(); } },
        { text: 'PDF',                                         icon: 'document-outline', handler: () => { void this.handleExportPdf(); }  },
        { text: this.translateService.instant('COMMON.CANCEL'), role: 'cancel' },
      ],
    });

    await actionSheet.present();
  }

  async shareImageAction(): Promise<void> {
    this.isShareDropdownOpen.set(false);
    await this.handleShareImage();
  }

  async exportPdfAction(): Promise<void> {
    this.isShareDropdownOpen.set(false);
    await this.handleExportPdf();
  }

  /**
   * Web-only: saves the share image as a PNG. The native app reaches the image
   * through openShareSheet's action sheet, which still uses the OS share sheet.
   */
  async downloadImageAction(): Promise<void> {
    this.closeShareModal();
    await this.handleDownloadImage();
  }

  closeShareModal(): void {
    this.isShareDropdownOpen.set(false);
    if (this.shareDialog?.nativeElement?.open) {
      this.shareDialog.nativeElement.close();
    }
  }

  onShareDialogBackdropClick(event: MouseEvent): void {
    if (event.target === this.shareDialog.nativeElement) {
      this.closeShareModal();
    }
  }

  get shareUrl(): string {
    return this.isBrowser ? window.location.href : '';
  }

  get pdfDownloadUrl(): string {
    if (!this.isBrowser) return '';
    return this.shareService.buildPdfDownloadUrl({
      startDate: this.currentChartRange.start.toJSDate(),
      endDate: this.currentChartRange.end.toJSDate(),
      energyType: this.toEnergyType(this.activeMarketType()),
      viewType: this.toViewType(this.activeTimeRange()),
      showAverage: this.showAverage(),
      lang: this.exportLang,
    });
  }

  get pdfFileName(): string {
    return `${this.exportFileBaseName}.pdf`;
  }

  get imageFileName(): string {
    return `${this.exportFileBaseName}.png`;
  }

  private get exportFileBaseName(): string {
    const market = this.activeMarketType();
    const marketPart = market === 'EL' ? 'el' : market === 'GAS' ? 'gas' : 'imbalance';
    const startDate = this.currentChartRange.start.toJSDate();
    const viewType = this.toViewType(this.activeTimeRange());
    let datePart: string;
    switch (viewType) {
      case ViewType.Week: {
        const isoWeek = this.getISOWeek(startDate);
        datePart = `week-${String(isoWeek).padStart(2, '0')}-${startDate.getFullYear()}`;
        break;
      }
      case ViewType.Month:
        datePart = `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}`;
        break;
      case ViewType.Year:
        datePart = String(startDate.getFullYear());
        break;
      default:
        datePart = `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}-${String(startDate.getDate()).padStart(2, '0')}`;
    }
    return `marktprijzen-${marketPart}-${datePart}`;
  }

  private getISOWeek(date: Date): number {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
  }

  shareViaLinkedIn(): void {
    if (!this.isBrowser) return;
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(this.shareUrl)}`, '_blank');
  }

  shareViaWhatsApp(): void {
    if (!this.isBrowser) return;
    window.open(`https://wa.me/?text=${encodeURIComponent(this.shareUrl)}`, '_blank');
  }

  shareViaMail(): void {
    if (!this.isBrowser) return;
    const subject = encodeURIComponent(this.translateService.instant('COMMON.SHARE_OR_DOWNLOAD'));
    const body = encodeURIComponent(this.shareUrl);
    window.open(`mailto:?subject=${subject}&body=${body}`);
  }

  async copyLink(): Promise<void> {
    if (!this.isBrowser) return;
    try {
      await navigator.clipboard.writeText(DayAheadWidgetComponent.PRICES_PAGE_URL);
      this.copyLinkSuccess.set(true);
      setTimeout(() => this.copyLinkSuccess.set(false), 2000);
    } catch {
      // Clipboard API unavailable
    }
  }

  /**
   * Shared metadata for both exports. `volume` decides the unit shown in the
   * title: the share image follows the UI's selection, while the PDF pins MWh
   * because its price query does.
   */
  private buildShareMeta(volume: VolumeEnum = this.activeVolume()) {
    const market = this.activeMarketType();

    const marketKey = market === 'EL' ? 'COMMON.PRICES.ELECTRIC'
      : market === 'GAS' ? 'COMMON.PRICES.GAS'
      : 'COMMON.PRICES.IMBALANCE';
    const marketLabel = this.translateService.instant(marketKey);
    const datasetLabel = `${marketLabel} – ${this.getTimeRangeLabel()}`;

    const source = market === 'EL' ? 'Epex Spot' : market === 'GAS' ? 'ZTP' : 'Elia';
    const date   = this.currentChartRange.start.setLocale(this.luxonLocale).toFormat('d MMMM yyyy');
    const unit   = volume === VolumeEnum.MWh ? '€/MWh' : '€/kWh';

    const titleLine1 = `${marketLabel} - ${source}`;
    const titleLine2 = `${date} - ${unit}`;

    const now = DateTime.now().setZone('Europe/Brussels');
    const timestampLabel = this.translateService.instant('COMMON.SNAPSHOT', {
      datetime: now.toFormat('dd/MM/yyyy HH:mm'),
    });

    const avg = this.tableAverageValue();
    const averageFormatted = `${this.translateService.instant('COMMON.AVERAGE')}: ${
      new Intl.NumberFormat('nl-BE', { style: 'currency', currency: 'EUR' }).format(avg)
    }`;

    return {
      titleLine1,
      titleLine2,
      datasetLabel,
      timestampLabel,
      showAverage: this.showAverage(),
      averageFormatted,
      showImbalanceDisclaimer: market === 'IMBALANCE',
      disclaimerText: this.translateService.instant('WIDGETS.DAYAHEAD.DISCLAIMER'),
    };
  }

  /**
   * The chart is rendered server-side from the same data the PDF uses, so no
   * canvas snapshot is captured here any more.
   */
  private buildShareImageCommand(): ExportShareImageCommand {
    const meta = this.buildShareMeta();

    return {
      startDate: this.currentChartRange.start.toJSDate(),
      endDate: this.currentChartRange.end.toJSDate(),
      energyType: this.toEnergyType(this.activeMarketType()),
      viewType: this.toViewType(this.activeTimeRange()),
      volume: this.activeVolume(),
      tableDateFormat: this.getPdfDateFormat(),
      titleLine1: meta.titleLine1,
      titleLine2: meta.titleLine2,
      datasetLabel: meta.datasetLabel,
      timestampLabel: meta.timestampLabel,
      showAverage: this.showAverage(),
      averageFormatted: meta.averageFormatted,
      showImbalanceDisclaimer: meta.showImbalanceDisclaimer,
      disclaimerText: meta.disclaimerText,
      includeTableData: this.activeView() === 'table',
      lang: this.exportLang,
    };
  }

  private async handleDownloadImage(): Promise<void> {
    const loading = await this.loadingCtrl.create({ spinner: 'crescent' });
    await loading.present();
    try {
      await this.shareService.downloadImageViaApi(this.buildShareImageCommand(), this.imageFileName);
    } finally {
      await loading.dismiss();
      loading.remove();
    }
  }

  private async handleShareImage(): Promise<void> {
    const loading = await this.loadingCtrl.create({ spinner: 'crescent' });
    await loading.present();
    try {
      const command = this.buildShareImageCommand();

      await this.shareService.shareImageViaApi(command);
    } finally {
      await loading.dismiss();
      loading.remove();
    }
  }

  private async handleExportPdf(): Promise<void> {
    const loading = await this.loadingCtrl.create({ spinner: 'crescent' });
    await loading.present();
    try {
      const market = this.activeMarketType();
      const locale = this.luxonLocale;

      const now            = DateTime.now().setZone('Europe/Brussels');
      const snapshotPrefix = this.translateService.instant('COMMON.PDF_SNAPSHOT_PREFIX');
      const snapshotLabel  = `${snapshotPrefix}\n${now.setLocale(locale).toFormat('d MMMM yyyy HH:mm')} CET`;

      // MWh: the PDF's price query is fixed to MWh, so its title must say so.
      const meta = this.buildShareMeta(VolumeEnum.MWh);

      const command: ExportPdfCommand = {
        titleLine1: meta.titleLine1,
        titleLine2: meta.titleLine2,
        startDate: this.currentChartRange.start.toJSDate(),
        endDate: this.currentChartRange.end.toJSDate(),
        energyType: this.toEnergyType(market),
        viewType: this.toViewType(this.activeTimeRange()),
        tableDateFormat: this.getPdfDateFormat(),
        datasetLabel: meta.datasetLabel,
        snapshotLabel,
        showAverage: this.showAverage(),
        showImbalanceDisclaimer: market === 'IMBALANCE',
        disclaimerText: this.translateService.instant('WIDGETS.DAYAHEAD.DISCLAIMER'),
        lang: this.exportLang,
      };

      await this.shareService.exportPdfViaApi(command);
    } finally {
      await loading.dismiss();
      loading.remove();
    }
  }

  private getPdfDateFormat(): string {
    switch (this.activeTimeRange()) {
      case 'DayQuarter':
      case 'DayHour': return 'HH:mm';
      case 'Week':
      case 'Month': return 'dd/MM';
      case 'Year': return 'MMMM';
    }
  }

  private toEnergyType(market: MarketType): EnergyType {
    switch (market) {
      case 'EL': return EnergyType.Electric;
      case 'GAS': return EnergyType.Gas;
      case 'IMBALANCE': return EnergyType.Imbalance;
    }
  }

  private toViewType(range: TimeRange): ViewType {
    switch (range) {
      case 'DayQuarter': return ViewType.Day;
      case 'DayHour': return ViewType.DayHour;
      case 'Week': return ViewType.Week;
      case 'Month': return ViewType.Month;
      case 'Year': return ViewType.Year;
    }
  }

  private toDateFilterRange(range: TimeRange): DateFilterRange {
    switch (range) {
      case 'DayQuarter':
      case 'DayHour': return 'Day';
      case 'Week': return 'Week';
      case 'Month': return 'Month';
      case 'Year': return 'Year';
    }
  }

  formatItemDate(item: PriceItemDto): string {
    if (!item.date) return '';
    const dt = DateTime.fromJSDate(item.date).setLocale(this.luxonLocale);
    switch (this.activeTimeRange()) {
      case 'DayQuarter':
      case 'DayHour': return dt.toFormat('HH:mm');
      case 'Week':
      case 'Month': return dt.toFormat('dd/MM');
      case 'Year': return dt.toFormat('MMMM');
      default: return dt.toFormat('dd/MM/yyyy');
    }
  }

  isMinPrice(price: number | undefined): boolean {
    return !!price && price === this.minValue() && this.displayedItems().length > 0;
  }

  isMaxPrice(price: number | undefined): boolean {
    return !!price && price === this.maxValue() && this.displayedItems().length > 0;
  }

  isTableMinPrice(price: number | undefined): boolean {
    return !!price && price === this.tableMinValue() && this.tableItems().length > 0;
  }

  isTableMaxPrice(price: number | undefined): boolean {
    return !!price && price === this.tableMaxValue() && this.tableItems().length > 0;
  }

  getLegendLabelKey(market: MarketType, timeRange: TimeRange): string {
    const rangeKey = timeRange === 'DayQuarter' ? 'DAY_QUARTER'
      : timeRange === 'DayHour' ? 'DAY_HOUR'
      : timeRange === 'Week' ? 'WEEK'
      : timeRange === 'Month' ? 'MONTH'
      : 'YEAR';
    return `WIDGETS.DAYAHEAD.LEGEND.${market}.${rangeKey}`;
  }

  getTimeRangeLabel(): string {
    switch (this.activeTimeRange()) {
      case 'DayQuarter': return this.translateService.instant('COMMON.DAY_QUARTER');
      case 'DayHour':    return this.translateService.instant('COMMON.DAY_HOUR');
      case 'Week':       return this.translateService.instant('COMMON.WEEK');
      case 'Month':      return this.translateService.instant('COMMON.MONTH');
      case 'Year':       return this.translateService.instant('COMMON.YEAR');
    }
  }

  private tryUpdateCurrentDate(date: DateTime): boolean {
    if (this.isDateDisabled(date)) {
      return false;
    }

    this.dateNavState.currentDate.set(date);
    return true;
  }

  private adjustCurrentDate(multiplier: number): void {
    const date = this.currentDate();
    const sign = Math.sign(multiplier);
    const isNegative = sign < 0;
    const absValue = Math.abs(multiplier);

    let modifier = {};
    switch (this.activeFilterRange()) {
      case 'Day':
        modifier = { days: absValue };
        break;
      case 'Week':
        modifier = { weeks: absValue };
        break;
      case 'Month':
        modifier = { months: absValue };
        break;
      case 'Year':
        modifier = { years: absValue };
        break;
      default:
        return;
    }

    const newDate = isNegative ? date.minus(modifier) : date.plus(modifier);
    this.tryUpdateCurrentDate(newDate);
  }
}

export type MarketType = 'EL' | 'GAS' | 'IMBALANCE';
export type TimeRange = 'DayQuarter' | 'DayHour' | 'Week' | 'Month' | 'Year';
export type WidgetView = 'chart' | 'table';

export interface WidgetApiPayload {
  startDate: Date;
  endDate: Date;
  energyType?: EnergyType;
  viewType: ViewType;
  filterRange: DateFilterRange;
  volume?: VolumeEnum;
  dayResolution?: 'quarter' | 'hour';
}
